import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Monitor,
    RefreshCw,
    AlertCircle,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    HelpCircle,
} from 'lucide-react';
import api from '../api/client';

// Traffic-light status the backend derives from avg-active-PCs / total_pcs
// (>=65% green, 35-65% orange, <35% red; "gray" when a café has no license
// and no registered PCs to score against). Kept in one place so the stat
// tiles, table rows and badges all agree on label/icon/color.
const STATUS_META = {
    green: { label: 'Healthy', icon: CheckCircle2, className: 'status-good' },
    orange: { label: 'Watch', icon: AlertTriangle, className: 'status-watch' },
    red: { label: 'Low Activity', icon: XCircle, className: 'status-low' },
    gray: { label: 'Not Scored', icon: HelpCircle, className: 'status-unscored' },
};

const DAY_OPTIONS = [3, 4, 7];

const formatShortDate = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function PCActiveBreakdown() {
    const navigate = useNavigate();

    const [days, setDays] = useState(4);
    const [cafes, setCafes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');

    const fetchStability = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get('/internal/fleet/pc-stability', { params: { days } });
            setCafes(response.data?.cafes || []);
        } catch (err) {
            console.error('Failed to fetch PC stability:', err);
            setError(err.response?.data?.detail || 'Failed to load active-PC breakdown');
        } finally {
            setIsLoading(false);
        }
    }, [days]);

    useEffect(() => {
        fetchStability();
        // Matches PCRegistry's live-refresh cadence; this data is a 5-min
        // rolling window server-side (_PC_STABILITY_TTL), so 60s is plenty.
        const interval = setInterval(fetchStability, 60000);
        return () => clearInterval(interval);
    }, [fetchStability]);

    const stats = useMemo(() => ({
        total: cafes.length,
        green: cafes.filter((c) => c.status === 'green').length,
        orange: cafes.filter((c) => c.status === 'orange').length,
        red: cafes.filter((c) => c.status === 'red').length,
        gray: cafes.filter((c) => c.status === 'gray').length,
    }), [cafes]);

    const filteredCafes = useMemo(
        () => (statusFilter === 'all' ? cafes : cafes.filter((c) => c.status === statusFilter)),
        [cafes, statusFilter]
    );

    return (
        <div className="pc-breakdown">
            <div className="page-header">
                <div className="page-header-content">
                    <div className="page-header-icon">
                        <Monitor size={24} />
                    </div>
                    <div>
                        <h1 className="page-title">Active PCs by Café</h1>
                        <p className="page-subtitle">
                            Active-PC count compared over the last {days} days — a café is only
                            colored once its count has held steady across that window.
                        </p>
                    </div>
                </div>
                <div className="page-header-actions">
                    <div className="days-toggle">
                        {DAY_OPTIONS.map((d) => (
                            <button
                                key={d}
                                className={`days-toggle__btn${days === d ? ' days-toggle__btn--active' : ''}`}
                                onClick={() => setDays(d)}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>
                    <button className="btn btn--ghost" onClick={fetchStability} disabled={isLoading}>
                        <RefreshCw size={16} className={isLoading ? 'spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="command-error-banner">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            <div className="pc-stats">
                <div className={`pc-stat pc-stat--total${statusFilter === 'all' ? ' pc-stat--active' : ''}`} onClick={() => setStatusFilter('all')}>
                    <Monitor size={20} />
                    <span className="pc-stat-value">{stats.total}</span>
                    <span className="pc-stat-label">Cafés</span>
                </div>
                <div className={`pc-stat pc-stat--good${statusFilter === 'green' ? ' pc-stat--active' : ''}`} onClick={() => setStatusFilter('green')}>
                    <CheckCircle2 size={20} />
                    <span className="pc-stat-value">{stats.green}</span>
                    <span className="pc-stat-label">Healthy</span>
                </div>
                <div className={`pc-stat pc-stat--watch${statusFilter === 'orange' ? ' pc-stat--active' : ''}`} onClick={() => setStatusFilter('orange')}>
                    <AlertTriangle size={20} />
                    <span className="pc-stat-value">{stats.orange}</span>
                    <span className="pc-stat-label">Watch</span>
                </div>
                <div className={`pc-stat pc-stat--low${statusFilter === 'red' ? ' pc-stat--active' : ''}`} onClick={() => setStatusFilter('red')}>
                    <XCircle size={20} />
                    <span className="pc-stat-value">{stats.red}</span>
                    <span className="pc-stat-label">Low Activity</span>
                </div>
            </div>

            <div className="table-wrapper">
                <table className="data-table breakdown-table">
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th>Café</th>
                            <th title={`Today's active PCs. Status color is based on the ${days}-day average.`}>Active PCs (Today)</th>
                            <th>Stability</th>
                            <th>Last {days} days</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && filteredCafes.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="empty-state">
                                    <div className="empty-state-content">
                                        <RefreshCw size={32} className="spin" strokeWidth={1} />
                                        <p>Loading active-PC breakdown…</p>
                                    </div>
                                </td>
                            </tr>
                        ) : filteredCafes.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="empty-state">
                                    <div className="empty-state-content">
                                        <Monitor size={48} strokeWidth={1} />
                                        <p>No cafés match this filter</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredCafes.map((cafe) => {
                                const meta = STATUS_META[cafe.status] || STATUS_META.gray;
                                const StatusIcon = meta.icon;
                                // Today's actual count (a real, whole PC count) -- the average
                                // across the window is used for the color/stability math below,
                                // but showing THAT as "Active PCs" reads as a fraction of a PC
                                // (e.g. 1.5) whenever the daily counts differ, which is confusing.
                                const today = cafe.daily_counts[cafe.daily_counts.length - 1];
                                const displayCount = today ? today.active_count : 0;
                                return (
                                    <tr key={cafe.cafe_id} className="breakdown-row">
                                        <td>
                                            <div className={`status-indicator ${meta.className}`}>
                                                <StatusIcon size={16} />
                                                <span>{meta.label}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <a
                                                href={`/cafes/${cafe.cafe_id}`}
                                                className="cafe-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(`/cafes/${cafe.cafe_id}`);
                                                }}
                                            >
                                                {cafe.cafe_name}
                                            </a>
                                        </td>
                                        <td>
                                            <span className="active-count">
                                                {displayCount} <span className="active-count__of">out of {cafe.total_pcs ?? '—'}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`stability-pill ${cafe.is_stable ? 'stability-pill--stable' : 'stability-pill--fluctuating'}`}>
                                                {cafe.is_stable ? 'Stable' : 'Fluctuating'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="daily-chips">
                                                {cafe.daily_counts.map((d) => (
                                                    <span key={d.date} className="daily-chip" title={d.date}>
                                                        <span className="daily-chip__date">{formatShortDate(d.date)}</span>
                                                        <span className="daily-chip__count">{d.active_count}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <style>{`
        .pc-breakdown {
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
          max-width: 1600px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: var(--space-4);
          margin-bottom: var(--space-8);
          flex-wrap: wrap;
        }

        .page-header-content {
          display: flex;
          align-items: center;
          gap: var(--space-4);
        }

        .page-header-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          background: var(--accent-gradient);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .page-title {
          font-size: var(--text-2xl);
          font-weight: 700;
          margin-bottom: var(--space-1);
        }

        .page-subtitle {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
          max-width: 520px;
        }

        .page-header-actions {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }

        .days-toggle {
          display: flex;
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          overflow: hidden;
        }

        .days-toggle__btn {
          padding: var(--space-2) var(--space-3);
          background: var(--bg-subtle);
          border: none;
          color: var(--text-secondary);
          font-size: var(--text-sm);
          cursor: pointer;
        }

        .days-toggle__btn + .days-toggle__btn {
          border-left: 1px solid var(--border-default);
        }

        .days-toggle__btn--active {
          background: var(--accent-primary);
          color: white;
        }

        .command-error-banner {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-4);
          margin-bottom: var(--space-6);
          background: var(--status-danger-subtle);
          color: var(--status-danger);
          border-radius: var(--radius-md);
          font-size: var(--text-sm);
        }

        .pc-stats {
          display: flex;
          gap: var(--space-4);
          margin-bottom: var(--space-6);
          flex-wrap: wrap;
        }

        .pc-stat {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-4) var(--space-6);
          background: var(--glass-bg);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all var(--duration-fast) var(--ease-out);
        }

        .pc-stat:hover {
          border-color: var(--border-hover);
          transform: translateY(-2px);
        }

        .pc-stat--total { color: var(--text-secondary); }
        .pc-stat--good { color: var(--status-success); }
        .pc-stat--watch { color: var(--status-warning); }
        .pc-stat--low { color: var(--status-danger); }

        .pc-stat--active {
          border-color: currentColor;
          box-shadow: 0 0 0 1px currentColor;
          background: var(--glass-bg-elevated);
        }

        .pc-stat-value {
          font-size: var(--text-xl);
          font-weight: 700;
          font-family: var(--font-mono);
        }

        .pc-stat-label {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
        }

        .table-wrapper {
          background: var(--glass-bg);
          backdrop-filter: blur(20px);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-lg);
          overflow: auto;
        }

        .breakdown-table {
          min-width: 900px;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-size: var(--text-sm);
        }

        .status-good { color: var(--status-success); }
        .status-watch { color: var(--status-warning); }
        .status-low { color: var(--status-danger); }
        .status-unscored { color: var(--text-tertiary); }

        .cafe-link {
          color: var(--accent-primary);
        }

        .cafe-link:hover {
          text-decoration: underline;
        }

        .active-count {
          font-family: var(--font-mono);
          font-weight: 600;
        }

        .active-count__of {
          font-family: var(--font-sans);
          font-weight: 400;
          color: var(--text-tertiary);
          font-size: var(--text-sm);
        }

        .stability-pill {
          display: inline-block;
          padding: 2px 10px;
          border-radius: var(--radius-full, 999px);
          font-size: var(--text-xs);
          font-weight: 600;
        }

        .stability-pill--stable {
          background: var(--status-success-subtle);
          color: var(--status-success);
        }

        .stability-pill--fluctuating {
          background: var(--status-warning-subtle);
          color: var(--status-warning);
        }

        .daily-chips {
          display: flex;
          gap: var(--space-2);
          flex-wrap: wrap;
        }

        .daily-chip {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 2px 8px;
          background: var(--bg-subtle);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-sm);
          min-width: 44px;
        }

        .daily-chip__date {
          font-size: 10px;
          color: var(--text-tertiary);
        }

        .daily-chip__count {
          font-size: var(--text-sm);
          font-weight: 600;
          font-family: var(--font-mono);
        }

        .empty-state {
          padding: var(--space-16) !important;
        }

        .empty-state-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-4);
          color: var(--text-tertiary);
        }
      `}</style>
        </div>
    );
}
